import {asIssueFieldSelection, type IssueFieldSelection} from '@github-ui/issue-fields-shared/issue-field'
import type {IssueFieldWithUnsavedValue} from '@github-ui/issue-metadata-types/issue-field-with-unsaved-value'
import {useEffect, useMemo} from 'react'

import {toIssueFieldWithUnsavedValue} from '../../../utils/issue-field-helper'

export interface UsePinnedFieldsStateOptions {
  pinnedFields: readonly IssueFieldSelection[] | null
  setFields: React.Dispatch<React.SetStateAction<IssueFieldWithUnsavedValue[]>>
  setFieldsSet: React.Dispatch<React.SetStateAction<string[]>>
}

export interface UsePinnedFieldsStateResult {
  pinnedFieldIds: Set<string>
}

/**
 * Custom hook that manages pinned fields state for issue types and organization-level pinned fields.
 * Handles filtering, mapping, and auto-population of pinned fields.
 */
export function usePinnedFieldsState({
  pinnedFields: pinnedFieldsInput,
  setFields,
  setFieldsSet,
}: UsePinnedFieldsStateOptions): UsePinnedFieldsStateResult {
  const pinnedFields = useMemo(() => {
    return (pinnedFieldsInput || []).map(field => toIssueFieldWithUnsavedValue(asIssueFieldSelection(field), null))
  }, [pinnedFieldsInput])

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const pinnedFieldIds = useMemo(() => new Set(pinnedFields.map(f => f.field.id!)), [pinnedFields])

  useEffect(() => {
    if (pinnedFields.length > 0) {
      // Auto-populate pinned fields when issue type or org pinned fields change
      setFields(prevFields => {
        // Create pinned fields, preserving existing values if they exist
        const fieldsWithValues = pinnedFields.map(pinnedField => {
          const existingField = prevFields.find(f => f.field.id === pinnedField.field.id)
          return existingField && existingField.value ? existingField : pinnedField
        })

        // Keep user-added fields that have values and are not pinned
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const userFields = prevFields.filter(f => !pinnedFieldIds.has(f.field.id!) && f.value)
        return [...fieldsWithValues, ...userFields]
      })
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setFieldsSet(pinnedFields.map(field => field.field.name!))
    } else {
      // Here we only need the manually added fields without the pinned ones
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setFields(prevFields => prevFields.filter(f => !pinnedFieldIds.has(f.field.id!) && f.value))
      setFieldsSet(prevFields => prevFields.filter(name => !pinnedFields.map(f => f.field.name).includes(name)))
    }
  }, [pinnedFieldsInput, pinnedFieldIds, pinnedFields, setFields, setFieldsSet])

  return {
    pinnedFieldIds,
  }
}
